import * as Yup from "yup";
import { getAppType } from "utils/helper";

const postalcodeRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|1\d|2\d|3[01])\/\d{4}$/;
const yearRegex = /^[0-9]{4}$/;
const sicRegex = /^[0-9]*$/;
const noSpecialCharRegex = /^[a-zA-Z ]+$/;
const floatNumber = /^(?:[1-9]\d*|0(?!(?:\.0+)?$))?(?:\.\d+)?$/;

export const currentEmployeesValidationSchema = Yup.object().shape({
  employees: Yup.array().of(
    Yup.object().shape(
      {
        firstName: Yup.string().when(
          ["lastName", "participantType", "gender", "dateOfBirth", "zip"],
          {
            is: (
              lastName: string,
              participantType: string,
              gender: string,
              dateOfBirth: string,
              zip: string
            ) =>
              !!lastName ||
              !!participantType ||
              !!gender ||
              !!dateOfBirth ||
              !!zip,
            then: Yup.string()
              .max(50, "Less than 50 characters, please.")
              .required("You missed this one")
              .matches(
                noSpecialCharRegex,
                "No special characters or numbers, please."
              ),
          }
        ),
        lastName: Yup.string().when(
          ["firstName", "participantType", "gender", "dateOfBirth", "zip"],
          {
            is: (
              firstName: string,
              participantType: string,
              gender: string,
              dateOfBirth: string,
              zip: string
            ) =>
              !!firstName ||
              !!participantType ||
              !!gender ||
              !!dateOfBirth ||
              !!zip,
            then: Yup.string()
              .max(50, "Less than 50 characters, please.")
              .required("You missed this one")
              .matches(
                noSpecialCharRegex,
                "No special characters or numbers, please."
              ),
          }
        ),
        participantType: Yup.string().when(
          ["firstName", "lastName", "gender", "dateOfBirth", "zip"],
          {
            is: (
              firstName: string,
              lastName: string,
              gender: string,
              dateOfBirth: string,
              zip: string
            ) =>
              !!firstName || !!lastName || !!gender || !!dateOfBirth || !!zip,
            then: Yup.string().required("You missed this one"),
          }
        ),
        gender: Yup.string().when(
          ["firstName", "lastName", "participantType", "dateOfBirth", "zip"],
          {
            is: (
              firstName: string,
              lastName: string,
              participantType: string,
              dateOfBirth: string,
              zip: string
            ) =>
              !!firstName ||
              !!lastName ||
              !!participantType ||
              !!dateOfBirth ||
              !!zip,
            then: Yup.string().required("You missed this one"),
          }
        ),
        dateOfBirth: Yup.string().when(
          ["firstName", "lastName", "participantType", "gender", "zip"],
          {
            is: (
              firstName: string,
              lastName: string,
              participantType: string,
              gender: string,
              zip: string
            ) =>
              !!firstName ||
              !!lastName ||
              !!participantType ||
              !!gender ||
              !!zip,
            then: Yup.string()
              .matches(
                dateRegex,
                "Please enter your birth date in the MM/DD/YYYY format"
              )
              .required("You missed this one")
              .test(
                "future date",
                "Hello time traveler! Please pick date in the past",
                (value) => new Date(value || "") <= new Date()
              ),
          }
        ),
        zip: Yup.string().when(
          ["firstName", "lastName", "participantType", "gender", "dateOfBirth"],
          {
            is: (
              firstName: string,
              lastName: string,
              participantType: string,
              gender: string,
              dateOfBirth: string
            ) =>
              !!firstName ||
              !!lastName ||
              !!participantType ||
              !!gender ||
              !!dateOfBirth,
            then: Yup.string()
              .matches(
                postalcodeRegex,
                "Zip code should be 5 digits or 5+4 digits. For example, 10041 or 10041-0004."
              )
              .required("You missed this one"),
          }
        ),
        currentPlanName: Yup.string().optional(),
        currentEnrollTier: Yup.string().optional(),
        currentMonthlyPremium: Yup.string()
          .optional()
          .matches(floatNumber, "Please enter a number greater than 0"),
        monthlyPremiumPostRenewal: Yup.string()
          .optional()
          .matches(floatNumber, "Please enter a number greater than 0"),
      },
      [
        // zip
        ["firstName", "zip"],
        ["lastName", "zip"],
        ["participantType", "zip"],
        ["gender", "zip"],
        ["dateOfBirth", "zip"],
        // dateOfBirth
        ["firstName", "dateOfBirth"],
        ["lastName", "dateOfBirth"],
        ["participantType", "dateOfBirth"],
        ["gender", "dateOfBirth"],
        ["zip", "dateOfBirth"],
        // gender
        ["firstName", "gender"],
        ["lastName", "gender"],
        ["participantType", "gender"],
        ["dateOfBirth", "gender"],
        ["zip", "gender"],
        // participantType
        ["firstName", "participantType"],
        ["lastName", "participantType"],
        ["gender", "participantType"],
        ["dateOfBirth", "participantType"],
        ["zip", "participantType"],
        // lastName
        ["firstName", "lastName"],
        ["participantType", "lastName"],
        ["gender", "lastName"],
        ["dateOfBirth", "lastName"],
        ["zip", "lastName"],
      ]
    )
  ),
});

export const currentInsuranceValidationSchema = Yup.object().shape({
  yearFounded: Yup.string()
    .matches(yearRegex, "Please enter a year in the YYYY format")
    .required("You missed this one")
    .test(
      "year validation",
      `Please enter a year between 1900-${new Date().getFullYear()}`,
      (value) =>
        Number(value) >= 1900 && Number(value) <= new Date().getFullYear()
    ),
  sicCode: Yup.string()
    .max(4, "SIC code is only 4 characters")
    .matches(sicRegex, "Numbers only, please.")
    .min(4, "SIC code is only 4 characters")
    .required("You missed this one"),
  controlledGroup: Yup.string().required("You missed this one"),
  startingCoverageDate: Yup.string()
    .matches(
      dateRegex,
      "Please enter your coverage date in the MM/DD/YYYY format"
    )
    .required("You missed this one")
    .test(
      "coverage year",
      "Great scott! Please pick a date in the future.",
      (value) => new Date(value || "") >= new Date()
    ),
  healthInsuranceInvoice: Yup.string().when("currentlyOfferHealthInsurance", {
    is: "Yes",
    then: Yup.string().required("You missed this one"),
  }),
  sbcDocument: Yup.string().when("currentlyOfferHealthInsurance", {
    is: "Yes",
    then: Yup.string().required("You missed this one"),
  }),
  renewalRateDocument: Yup.string().when("currentRenewalDate", {
    is: true,
    then: Yup.string().required("You missed this one"),
  }),
  isOnPeo: Yup.string().when("currentlyOfferHealthInsurance", {
    is: (currentlyOfferHealthInsurance: string) =>
      currentlyOfferHealthInsurance === "Yes" &&
      getAppType() !== "HEALTH_INSURANCE_UPSELL",
    then: Yup.string().required("You missed this one"),
  }),
  currentPeo: Yup.string().when("isOnPeo", {
    is: "Yes",
    then: Yup.string().required("You missed this one"),
  }),
  currentPeoOther: Yup.string().when("currentPeo", {
    is: "Other",
    then: Yup.string().required("You missed this one"),
  }),
});

export const employeeDependentValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .max(50, "Less than 50 characters, please.")
    .required("You missed this one")
    .matches(noSpecialCharRegex, "No special characters or numbers, please."),
  lastName: Yup.string()
    .trim()
    .max(50, "Less than 50 characters, please.")
    .required("You missed this one")
    .matches(noSpecialCharRegex, "No special characters or numbers, please."),
  homeZip: Yup.string()
    .matches(
      postalcodeRegex,
      "Zip code should be 5 digits or 5+4 digits. For example, 10041 or 10041-0004."
    )
    .required("You missed this one"),
  gender: Yup.string().required("You missed this one"),
  participantType: Yup.string().required("You missed this one"),
  dateOfBirth: Yup.string()
    .matches(dateRegex, "Please enter your birth date in the MM/DD/YYYY format")
    .required("You missed this one")
    .test(
      "future date",
      "Hello time traveler! Please pick date in the past",
      (value) => new Date(value || "") <= new Date()
    ),
});
